import { PROVIDER, UserResponse } from '../../../providers/User/User.model';
import { SliceOrderRate } from '../../features/orders/ordersSlice';
import { BaseQueryParams } from '../../Global.model';
import { AvailabilityStatus } from '../availability/availability.model';
import { CarrierResponse } from '../carriers/carriers.model';
import { DriverFile } from '../driverFile/driverFile.model';
import { DriverProfile } from '../driverProfile/driverProfile.model';
import { EldConnectionInfo, EldDriver, HoursOfService } from '../eld/eld.model';
import { Equipment } from '../equipment/equipment.model';
import { FuelCard } from '../fuelcard/fuelcard.model';
import { OrderRate } from '../orders/orders.model';
import { OwnerOperator } from '../ownerOperators/ownerOperators.model';
import { ISettlementDriverLoadAdjustmentType } from '../settlement/settlement.model';
import { STOP_TYPE, TRIP_DRIVER_PAY_TYPE, TRIP_DRIVER_PAY_UNIT } from '../trips/trips.model';

export enum DRIVER_EMPLOYMENT_CLASSIFICATION {
  CONTRACTOR = 'CONTRACTOR',
  EMPLOYEE = 'EMPLOYEE'
}

export enum DRIVER_TYPE {
  OWNER_OPERATOR_DRIVER = 'OWNER_OPERATOR_DRIVER',
  COMPANY_DRIVER = 'COMPANY_DRIVER'
}

export enum DRIVER_DEFAULT_PAY_RATE_TYPE {
  PERCENTAGE = 'PERCENTAGE',
  PER_HOUR = 'PER_HOUR',
  PER_MILE = 'PER_MILE',
  FLAT = 'FLAT'
}

export interface AvailabilityStatusData {
  status: AvailabilityStatus;
  unavailable_start_date?: string;
  unavailable_end_date?: string;
}

/**
 * Driver information
 */
export interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  initials: string;
  email_address?: string;
  phone?: string;
  company_name?: string;
  custom_driver_id?: string;
  loads_completed?: string;
  deleted_at?: null;
  tenant_id?: string;
  default_truck_id?: string;
  default_truck?: Equipment;
  default_trailer?: Equipment;
  default_trailer_id?: string;
  default_pay_rate_type?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT;
  load_driver_pay_other_payment?: ISettlementDriverLoadAdjustmentType[];
  default_load_driver_other_pay?: ISettlementDriverLoadAdjustmentType[];
  readable_pay_rate_type?: string;
  pay_rate?: string;
  formatted_pay_rate: string;
  type: DRIVER_TYPE | null;
  carrier_id?: string;
  carrier?: CarrierResponse;
  twuser_id?: string;
  twuser?: TwUserDriver;
  deduct_fuel_purchase_on_settlement?: boolean;
  paid_ytd?: number;
  notes?: string;
  availability_status: AvailabilityStatusData;
  driver_profile: DriverProfile | null;
  driver_files: DriverFile[] | null;
  eld_connection?: EldConnectionInfo;
  eld_connection_id?: string;
  eld_data?: EldDriver;
  hours_of_service?: HoursOfService;
  fuel_card?: FuelCard;
  employment_classification: DRIVER_EMPLOYMENT_CLASSIFICATION | null;
  owner_operator_id?: string;
  owner_operator: OwnerOperator | null;
  // TODO bring back when availability is refactored
  // availability_status: AvailabilityStatus | null;
}

/**
 * Driver creation data for request
 */
export interface CreateDriverParams {
  first_name: string;
  last_name: string;
  email_address?: string;
  phone?: string | null;
  company_name?: string;
  default_truck_id?: string;
  default_trailer_id?: string;
  default_pay_rate_type?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT;
  pay_rate?: string;
  type?: DRIVER_TYPE;
  carrier_id?: string;
  create_user_and_send_invite?: boolean;
  deduct_fuel_purchase_on_settlement?: boolean;
  eld_connection_id?: string | null;
  eld_data?: EldDriver | null;
  fuel_card_id?: string | null;
  owner_operator_id?: string | null;
  employment_classification?: DRIVER_EMPLOYMENT_CLASSIFICATION | null;
  custom_driver_id?: string | null;
}

/**
 * Driver data required from user in creation
 */
export interface CreateDriverData {
  firstName: string;
  lastName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  driverCustomId?: string;
  defaultTruckId?: string;
  defaultTrailerId?: string;
  defaultPayRateType?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT;
  payRate?: string;
  type: DRIVER_TYPE | null;
  carrierId?: string;
  createUserAndSendInvite?: boolean;
  deductFuelPurchaseOnSettlement?: boolean;
  notes?: string;
  eldConnectionId?: string;
  eldData?: EldDriver;
  fuelCardId?: string;
  ownerOperatorId?: string | null;
  employmentClassification?: DRIVER_EMPLOYMENT_CLASSIFICATION | null;
  ownerOperator?: OwnerOperator | null;
}

/**
 * Driver data required from user in contact details
 */
export type DriverContactDetails = Pick<CreateDriverData, 'firstName' | 'lastName'> &
  Partial<Pick<CreateDriverData, 'email' | 'phone' | 'driverCustomId'>>;

/**
 * Driver data required from user in payment info
 */
export type DriverPaymentInfo = Partial<
  Pick<
    CreateDriverData,
    | 'type'
    | 'employmentClassification'
    | 'carrierId'
    | 'companyName'
    | 'defaultPayRateType'
    | 'payRate'
    | 'deductFuelPurchaseOnSettlement'
    | 'fuelCardId'
    | 'ownerOperatorId'
  >
>;

export type DriverOperations = Partial<
  Pick<
    CreateDriverData,
    'defaultTruckId' | 'defaultTrailerId' | 'eldConnectionId' | 'eldData'
    // TODO: | 'dispatchGroupId'
    // TODO: | 'tracking'
  >
>;

export type DriverNotes = Partial<Pick<CreateDriverData, 'notes'>>;

export type DriverDialogWarningData = Pick<CreateDriverData, 'firstName' | 'lastName'>;

export type TwUserDriver = Pick<UserResponse, 'status'>;

export interface FilterParams {
  search?: string;
  driverId?: string;
  carrierId?: string;
  driverFileStatus?: string[];
}

export interface SearchDriverParams {
  filters?: FilterParams;
  params?: BaseQueryParams;
}

export interface DriverPayCalculationParams {
  driver_id: string;
  owner_operator_id?: string;
  trip_id?: string;
  order_sort_order?: number;
  division_id?: string;
  order_rates?: {
    invoice_service_id: string;
    quantity: number;
    rate: number;
    unit: TRIP_DRIVER_PAY_UNIT;
  }[];
  trips?: {
    order: number;
    empty_miles: number | null;
    estimated_mileage: number | null;
    is_empty_miles_auto: boolean;
    is_loaded_miles_auto: boolean;
    loaded_mileage_source?: PROVIDER | null;
    empty_mileage_source?: PROVIDER | null;
    stops: {
      order: number;
      sort_order: number;
      // start_date?: string;
      location_id?: string | null;
    }[];
    drivers: {
      id: string;
    }[];
    truck_id?: string;
  }[];
  order_ids?: string[];
}

export interface DriverPayCalculationResponse {
  trip_driver_base_pay: {
    pay_type: TRIP_DRIVER_PAY_TYPE;
    rate: number;
    unit: TRIP_DRIVER_PAY_UNIT;
    loaded_miles: number;
    empty_miles?: number;
    trip_revenue_percentage: number;
    team_driver_percentage: number;
    overriden_order_rate: number;
    qbo_service_id: string | null;
    invoice_id: string | null;
    driver_id: string;
  };
  trip_driver_other_pay: {
    description: string;
    pay_type: TRIP_DRIVER_PAY_TYPE;
    rate: number;
    unit: TRIP_DRIVER_PAY_UNIT;
    quantity: number;
  }[];
}
